import { useMemo } from 'react'
import * as THREE from 'three'

export interface PropsPistaAsfalto3D {
  /** Permite ocultar marcas de frenagem se desejado */
  mostrarMarcasFrenagem?: boolean
}

const TRACOS_CENTRAIS_Z = [-10, -6, -2, 2, 6, 10] as const

/**
 * Pista de asfalto com faixas viárias, acostamento e marcas de frenagem
 * para contextualização realista de acidentes veiculares no Tabletop.
 *
 * Dimensões (Padrão Rodovia Brasileira):
 * - Comprimento: 24m (Z de -12m a +12m)
 * - Largura total: 10m (X de -5m a +5m)
 *   - 2 faixas de rolamento de 3,5m (X de -3,5m a +3,5m)
 *   - Acostamentos de 1,5m em cada lateral (X de -5m a -3,5m e de +3,5m a +5m)
 * - Nivelamento: Y ~ 0 (com elevação infinitesimal de 1-2mm para evitar z-fighting
 *   com o grid, e sem degraus para não obstruir o arraste livre de objetos e vítimas).
 */
export function PistaAsfalto3D({ mostrarMarcasFrenagem = true }: PropsPistaAsfalto3D) {
  // Ignora eventos de ponteiro para que os cliques no chão (aoTocarChao)
  // e arraste de peças passem diretamente para o plano de interação
  const noRaycast = () => null

  return (
    <group position={[0, 0, 0]}>
      {/* 0. Oclusão da grade do estúdio. As chapas abaixo não escrevem
          profundidade, e a grade (translúcida, desenhada depois de tudo que é
          opaco) apareceria por cima da pista e listraria as faixas. Esta chapa
          não pinta nada: só escreve profundidade 2 mm abaixo do asfalto, o que
          basta para a grade (a 4 mm) falhar no teste de profundidade sob a
          pista e continuar visível ao redor dela. */}
      <mesh
        position={[0, -0.002, 0]}
        rotation={[-Math.PI / 2, 0, 0]}
        raycast={noRaycast}
      >
        <planeGeometry args={[10.0, 24.0]} />
        <meshBasicMaterial colorWrite={false} />
      </mesh>

      {/* 1. Acostamento Esquerdo (X: -5.0 a -3.5) */}
      <mesh
        position={[-4.25, 0.0008, 0]}
        rotation={[-Math.PI / 2, 0, 0]}
        raycast={noRaycast}
      >
        <planeGeometry args={[1.5, 24.0]} />
        <meshStandardMaterial
          color="#292f39"
          roughness={0.96}
          metalness={0.03}
          depthWrite={false}
        />
      </mesh>

      {/* 2. Acostamento Direito (X: +3.5 a +5.0) */}
      <mesh
        position={[4.25, 0.0008, 0]}
        rotation={[-Math.PI / 2, 0, 0]}
        raycast={noRaycast}
      >
        <planeGeometry args={[1.5, 24.0]} />
        <meshStandardMaterial
          color="#292f39"
          roughness={0.96}
          metalness={0.03}
          depthWrite={false}
        />
      </mesh>

      {/* 3. Pista Principal de Asfalto (2 faixas de rolamento, X: -3.5 a +3.5) */}
      <mesh
        position={[0, 0.001, 0]}
        rotation={[-Math.PI / 2, 0, 0]}
        raycast={noRaycast}
      >
        <planeGeometry args={[7.0, 24.0]} />
        <meshStandardMaterial
          color="#1e232b"
          roughness={0.92}
          metalness={0.05}
          depthWrite={false}
        />
      </mesh>

      {/* 4. Linha de Bordo Esquerda (Branca contínua delimitando acostamento) */}
      <mesh
        position={[-3.5, 0.002, 0]}
        rotation={[-Math.PI / 2, 0, 0]}
        raycast={noRaycast}
      >
        <planeGeometry args={[0.12, 24.0]} />
        <meshBasicMaterial
          color="#f8fafc"
          transparent
          opacity={0.92}
          depthWrite={false}
        />
      </mesh>

      {/* 5. Linha de Bordo Direita (Branca contínua delimitando acostamento) */}
      <mesh
        position={[3.5, 0.002, 0]}
        rotation={[-Math.PI / 2, 0, 0]}
        raycast={noRaycast}
      >
        <planeGeometry args={[0.12, 24.0]} />
        <meshBasicMaterial
          color="#f8fafc"
          transparent
          opacity={0.92}
          depthWrite={false}
        />
      </mesh>

      {/* 6. Linha Central Tracejada (Amarela padrão Contran / DNIT para fluxos opostos) */}
      {TRACOS_CENTRAIS_Z.map((z) => (
        <mesh
          key={`faixa-central-${z}`}
          position={[0, 0.002, z]}
          rotation={[-Math.PI / 2, 0, 0]}
          raycast={noRaycast}
        >
          <planeGeometry args={[0.12, 2.0]} />
          <meshBasicMaterial
            color="#f59e0b"
            transparent
            opacity={0.94}
            depthWrite={false}
          />
        </mesh>
      ))}

      {/* 7. Guias/Limites externos do acostamento (delimitador sutil de terra/borda) */}
      <mesh
        position={[-5.02, 0.0012, 0]}
        rotation={[-Math.PI / 2, 0, 0]}
        raycast={noRaycast}
      >
        <planeGeometry args={[0.06, 24.0]} />
        <meshBasicMaterial
          color="#3f4756"
          transparent
          opacity={0.75}
          depthWrite={false}
        />
      </mesh>
      <mesh
        position={[5.02, 0.0012, 0]}
        rotation={[-Math.PI / 2, 0, 0]}
        raycast={noRaycast}
      >
        <planeGeometry args={[0.06, 24.0]} />
        <meshBasicMaterial
          color="#3f4756"
          transparent
          opacity={0.75}
          depthWrite={false}
        />
      </mesh>

      {/* 8. Marcas de Frenagem / Derrapagem de Borracha na Pista */}
      {mostrarMarcasFrenagem && (
        <group position={[0, 0.0025, 0]}>
          {/* Marca de pneu esquerdo (frenagem forte até colisão) */}
          <mesh
            position={[-0.72, 0, -1.0]}
            rotation={[-Math.PI / 2, 0, 0.02]}
            raycast={noRaycast}
          >
            <planeGeometry args={[0.22, 5.2]} />
            <meshBasicMaterial
              color="#0d1117"
              transparent
              opacity={0.78}
              depthWrite={false}
            />
          </mesh>

          {/* Marca de pneu direito (frenagem forte com leve desvio instintivo) */}
          <mesh
            position={[0.74, 0, -0.9]}
            rotation={[-Math.PI / 2, 0, -0.015]}
            raycast={noRaycast}
          >
            <planeGeometry args={[0.22, 5.0]} />
            <meshBasicMaterial
              color="#0d1117"
              transparent
              opacity={0.82}
              depthWrite={false}
            />
          </mesh>

          {/* Marcas de atrito/derrapagem em arco próximo ao ponto de impacto */}
          <mesh
            position={[-0.35, 0, 1.4]}
            rotation={[-Math.PI / 2, 0, 0.38]}
            raycast={noRaycast}
          >
            <planeGeometry args={[0.26, 1.8]} />
            <meshBasicMaterial
              color="#0d1117"
              transparent
              opacity={0.65}
              depthWrite={false}
            />
          </mesh>
          <mesh
            position={[0.45, 0, 1.55]}
            rotation={[-Math.PI / 2, 0, -0.28]}
            raycast={noRaycast}
          >
            <planeGeometry args={[0.26, 1.6]} />
            <meshBasicMaterial
              color="#0d1117"
              transparent
              opacity={0.68}
              depthWrite={false}
            />
          </mesh>
        </group>
      )}
    </group>
  )
}
